import { initializeApp } from 'firebase/app';
import { ReCaptchaEnterpriseProvider, initializeAppCheck } from 'firebase/app-check';
import {
  GoogleAuthProvider,
  connectAuthEmulator,
  getAuth,
  onIdTokenChanged,
  signOut,
  signInWithPopup,
  type User,
} from 'firebase/auth';
import {
  collection,
  connectFirestoreEmulator,
  doc,
  getDoc,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type DocumentData,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};
export const firebaseConfigured = Boolean(config.apiKey && config.projectId);
const app = firebaseConfigured ? initializeApp(config) : null;
export const appCheckConfigured = Boolean(import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY);
if (import.meta.env.DEV && import.meta.env.VITE_APPCHECK_DEBUG === 'true')
  Object.assign(self, { FIREBASE_APPCHECK_DEBUG_TOKEN: true });
if (app && appCheckConfigured)
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
export const auth = app ? getAuth(app) : null;
export const firestore = app ? getFirestore(app) : null;
export const functions = app ? getFunctions(app, 'europe-west1') : null;
if (app && import.meta.env.VITE_USE_EMULATORS === 'true' && auth && firestore && functions) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}
export async function googleLogin() {
  if (!auth || !functions) return { profile: { status: 'ACTIVE' } };
  if (!appCheckConfigured)
    throw new Error('App Check is not configured. Set VITE_RECAPTCHA_ENTERPRISE_SITE_KEY.');
  const provider = new GoogleAuthProvider();
  const credential = await signInWithPopup(auth, provider);
  return registerCurrentUser(credential.user);
}

async function registerCurrentUser(user: User) {
  if (!functions) throw new Error('Firebase Functions is not configured.');
  const response = await httpsCallable(
    functions,
    'registerOrGetProfile',
  )({ displayName: user.displayName ?? undefined });
  await user.getIdToken(true);
  return { profile: response.data as { status: string } };
}

export async function refreshProfile() {
  if (!auth?.currentUser) throw new Error('Nincs bejelentkezett felhasználó.');
  if (!appCheckConfigured)
    throw new Error('App Check is not configured. Set VITE_RECAPTCHA_ENTERPRISE_SITE_KEY.');
  return registerCurrentUser(auth.currentUser);
}

export type UserProfile = {
  uid: string;
  displayName: string;
  email: string;
  role: 'ADMIN' | 'OPERATOR';
  status: 'ACTIVE' | 'PENDING' | 'REJECTED' | 'SUSPENDED';
};

export type TransferDocument = DocumentData & { id: string };
export type NotificationDocument = DocumentData & { id: string };
export type AuditLogDocument = DocumentData & { id: string };

export function subscribeUsers(
  callback: (users: UserProfile[]) => void,
  onError: (error: Error) => void,
) {
  if (!firestore) return () => undefined;
  return onSnapshot(
    collection(firestore, 'users'),
    (snapshot) =>
      callback(snapshot.docs.map((item) => ({ uid: item.id, ...item.data() }) as UserProfile)),
    onError,
  );
}

export function subscribeTransfers(
  callback: (transfers: TransferDocument[]) => void,
  onError: (error: Error) => void,
) {
  if (!firestore) return () => undefined;
  return onSnapshot(
    collection(firestore, 'transfers'),
    (snapshot) => callback(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
    onError,
  );
}

export function subscribeNotifications(
  callback: (notifications: NotificationDocument[]) => void,
  onError: (error: Error) => void,
) {
  if (!firestore) return () => undefined;
  return onSnapshot(
    query(collection(firestore, 'notifications'), orderBy('createdAt', 'desc'), limit(8)),
    (snapshot) => callback(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
    onError,
  );
}

export type StatusLogChange = { field: string; from: unknown; to: unknown };
export type StatusLogEntry = {
  id: string;
  at?: Date;
  trigger: string;
  source: string;
  actor: { uid: string; email: string | null } | null;
  changes: StatusLogChange[];
  evidence: Record<string, unknown>;
  flightEventId: string | null;
};

export function subscribeStatusLog(
  transferId: string,
  callback: (entries: StatusLogEntry[]) => void,
  onError: (error: Error) => void,
) {
  if (!firestore) return () => undefined;
  return onSnapshot(
    query(
      collection(firestore, 'transfers', transferId, 'statusLog'),
      orderBy('at', 'desc'),
      limit(100),
    ),
    (snapshot) =>
      callback(
        snapshot.docs.map((item) => {
          const data = item.data();
          return {
            id: item.id,
            at: data.at?.toDate?.(),
            trigger: data.trigger,
            source: data.source,
            actor: data.actor ?? null,
            changes: Array.isArray(data.changes) ? data.changes : [],
            evidence: data.evidence ?? {},
            flightEventId: data.flightEventId ?? null,
          };
        }),
      ),
    onError,
  );
}

export function subscribeAuditLogs(
  callback: (logs: AuditLogDocument[]) => void,
  onError: (error: Error) => void,
) {
  if (!firestore) return () => undefined;
  return onSnapshot(
    query(collection(firestore, 'auditLogs'), orderBy('timestamp', 'desc'), limit(50)),
    (snapshot) => callback(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
    onError,
  );
}

export type AeroDataBoxCredits = {
  lastCheckedAt?: unknown;
  apiUsage?: {
    quota: number;
    apiCallsMade: number;
    apiCallsLeft: number;
  };
  apiUsageError?: string;
  alertCreditBalance?: {
    creditsRemaining: number;
    lastRefilledUtc?: string;
    lastDeductedUtc?: string;
  };
  alertCreditError?: string;
};
export function subscribeAeroStatus(
  callback: (data: {
    subscriptions: TransferDocument[];
    events: TransferDocument[];
    credits: AeroDataBoxCredits | null;
  }) => void,
  onError: (error: Error) => void,
) {
  if (!firestore) return () => undefined;
  let subscriptions: TransferDocument[] = [];
  let events: TransferDocument[] = [];
  let transferEvents: TransferDocument[] = [];
  let credits: AeroDataBoxCredits | null = null;
  const emitWithTransferEvents = () =>
    callback({
      subscriptions,
      events: [...events, ...transferEvents],
      credits,
    });
  const unsubscribeSubscriptions = onSnapshot(
    collection(firestore, 'flightSubscriptions'),
    (snapshot) => {
      subscriptions = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      emitWithTransferEvents();
    },
    onError,
  );
  const unsubscribeEvents = onSnapshot(
    query(collection(firestore, 'flightEvents'), orderBy('createdAt', 'desc'), limit(50)),
    (snapshot) => {
      events = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
      emitWithTransferEvents();
    },
    onError,
  );
  const unsubscribeTransfers = onSnapshot(
    collection(firestore, 'transfers'),
    (snapshot) => {
      transferEvents = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }) as TransferDocument)
        .filter((item) => item.provider?.lastCheckedAt)
        .map((item) => ({
          id: `transfer-${item.id}`,
          source: item.provider?.lastError ? 'RECONCILIATION_ERROR' : 'RECONCILIATION',
          flightNumber: item.manual?.flightNumber,
          normalized: {
            flightCount: Array.isArray(item.provider?.matches) ? item.provider.matches.length : 0,
            selectedStatus: item.computed?.status,
            delayMinutes: item.computed?.delayMinutes ?? 0,
            estimatedUtc: item.computed?.estimatedEventUtc ?? null,
            error: item.provider?.lastError,
          },
          createdAt: item.provider.lastCheckedAt,
        }));
      emitWithTransferEvents();
    },
    onError,
  );
  const unsubscribeCredits = onSnapshot(
    doc(firestore, 'system', 'aerodatabox'),
    (snapshot) => {
      credits = snapshot.exists() ? (snapshot.data() as AeroDataBoxCredits) : null;
      emitWithTransferEvents();
    },
    onError,
  );
  return () => {
    unsubscribeSubscriptions();
    unsubscribeEvents();
    unsubscribeTransfers();
    unsubscribeCredits();
  };
}

export async function changeUserAccess(
  uid: string,
  status: 'ACTIVE' | 'REJECTED' | 'SUSPENDED',
  role: 'ADMIN' | 'OPERATOR',
) {
  if (!functions) throw new Error('Firebase Functions is not configured.');
  await httpsCallable(functions, 'updateUserAccess')({ uid, status, role });
}

export async function saveTransfer(transfer: Record<string, unknown>, id?: string) {
  if (!functions) throw new Error('Firebase Functions is not configured.');
  const callable = httpsCallable(functions, id ? 'updateTransfer' : 'createTransfer');
  return callable(id ? { id, transfer } : transfer);
}

export async function removeTransfer(id: string) {
  if (!functions) throw new Error('Firebase Functions is not configured.');
  await httpsCallable(functions, 'deleteTransfer')({ id });
}

export async function refreshTransferStatus(id: string) {
  if (!functions) throw new Error('Firebase Functions is not configured.');
  const result = await httpsCallable(functions, 'refreshTransferStatus')({ id });
  return result.data as { computed: DocumentData | null; provider: DocumentData | null };
}

export type NormalizedFlightMatch = {
  status: string;
  departure: { airport: string; scheduledUtc?: string; estimatedUtc?: string };
  arrival: { airport: string; scheduledUtc?: string; estimatedUtc?: string };
};

export async function lookupFlight(flightNumber: string, dateLocal: string) {
  if (!functions) throw new Error('Firebase Functions is not configured.');
  const result = await httpsCallable(functions, 'lookupFlight')({ flightNumber, dateLocal });
  return result.data as { matches: NormalizedFlightMatch[] };
}

export async function loadSettings(path: 'user' | 'global', uid?: string) {
  if (!firestore) throw new Error('Firestore is not configured.');
  const ref =
    path === 'user' ? doc(firestore, 'userSettings', uid!) : doc(firestore, 'settings', 'global');
  const snapshot = await getDoc(ref);
  return snapshot.exists() ? snapshot.data() : null;
}

export async function saveSettings(
  path: 'user' | 'global',
  values: Record<string, unknown>,
  uid?: string,
) {
  if (!firestore) throw new Error('Firestore is not configured.');
  const ref =
    path === 'user' ? doc(firestore, 'userSettings', uid!) : doc(firestore, 'settings', 'global');
  await setDoc(ref, { ...values, updatedAt: serverTimestamp() }, { merge: true });
}

export async function sendTestFlightEmail(to?: string) {
  if (!functions) throw new Error('Firebase Functions is not configured.');
  const recipient = to?.trim();
  return httpsCallable(
    functions,
    'sendTestFlightEmail',
  )({
    to: recipient || undefined,
    eventType: 'DELAY',
  });
}

export async function diagnoseEmailConfig() {
  if (!functions) throw new Error('Firebase Functions is not configured.');
  return httpsCallable(functions, 'diagnoseEmailConfig')();
}

export function observeAuth(
  callback: (user: User | null, approved: boolean, role: string | null) => void,
) {
  if (!auth) {
    callback(null, false, null);
    return () => undefined;
  }
  return onIdTokenChanged(auth, async (user) => {
    const claims = user ? (await user.getIdTokenResult()).claims : {};
    callback(user, claims.approved === true, typeof claims.role === 'string' ? claims.role : null);
  });
}

export async function logout() {
  if (auth) await signOut(auth);
}

export type { User };
